/**
 * Tiny GA4 helper that gracefully no-ops when gtag isn't loaded
 * (dev environments, ad-blocker users, GA4 not yet configured).
 *
 * Usage:
 *   import { trackEvent } from "../lib/analytics";
 *   trackEvent("funnel_unlock", { source: "free_buddy_funnel" });
 *
 * Fires once-per-session events via `trackOnce("event_name", ...)` for
 * mount-time events (page views, hero impressions) so reloading the same
 * tab doesn't pollute funnel counts.
 */

const SESSION_FIRED = new Set();

export function trackEvent(name, params = {}) {
  try {
    if (typeof window === "undefined") return;
    // window.gtag is injected by the GA4 snippet in /public/index.html when
    // REACT_APP_GA_ID is set. If it's missing we silently skip — no errors.
    if (typeof window.gtag !== "function") return;
    window.gtag("event", name, params);
  } catch (err) {
    // Analytics must never break the app.
    console.warn("[analytics] event failed:", err);
  }
}

export function trackOnce(name, params = {}) {
  const key = `${name}::${JSON.stringify(params)}`;
  if (SESSION_FIRED.has(key)) return;
  SESSION_FIRED.add(key);
  trackEvent(name, params);
}

/**
 * GA4 ecommerce helpers — standardized field names so Google's built-in
 * "Monetization" and "Purchase" reports populate automatically.
 *
 * Sources: https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */

function _mapCartItem(item) {
  return {
    item_id: item.product_id,
    item_name: item.name || item.product_id,
    price: Number(item.unit_price || 0),
    quantity: Number(item.quantity || 1),
    // Custom dims — great for slicing in GA4 explorations later.
    custom_buddy_id: item.custom_buddy_id || undefined,
    custom_buddy_name: item.custom_buddy_name || undefined,
  };
}

export function trackAddToCart(item) {
  const mapped = _mapCartItem(item);
  trackEvent("add_to_cart", {
    currency: "USD",
    value: mapped.price * mapped.quantity,
    items: [mapped],
  });
}

export function trackRemoveFromCart(item) {
  const mapped = _mapCartItem(item);
  trackEvent("remove_from_cart", {
    currency: "USD",
    value: mapped.price * mapped.quantity,
    items: [mapped],
  });
}

export function trackBeginCheckout({ items, total, coupon }) {
  trackEvent("begin_checkout", {
    currency: "USD",
    value: Number(total || 0),
    coupon: coupon || undefined,
    items: (items || []).map(_mapCartItem),
  });
}

export function trackPurchase({ transaction_id, value, currency = "USD", items, coupon }) {
  trackOnce("purchase", {
    transaction_id,
    value: Number(value || 0),
    currency,
    coupon: coupon || undefined,
    items: (items || []).map(_mapCartItem),
  });
}
